import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { useNavigate } from 'react-router-dom';
import Header from '../components/Header';
import Footer from '../components/Footer';
import BlogCard from '../components/BlogCard';
import CTABanner from '../components/CTABanner';
import { Search, Filter, ChevronLeft, ChevronRight, Loader2 } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const categories = [
  'All', 'Costs & Pricing', 'Legal & Compliance', 'Speed & Process',
  'Workers & Industries', 'Volume & Flexibility', 'Payroll & HR',
  'Quality & Reliability', 'Comparing Options', 'Location & Regional',
  'Misconceptions & Concerns', 'Industry News',
];

const BlogPage = () => {
  const navigate = useNavigate();
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [activeCategory, setActiveCategory] = useState('All');
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);

  const fetchPosts = async () => {
    setLoading(true);
    try {
      const params = { page, limit: 9 };
      if (activeCategory !== 'All') params.category = activeCategory;
      if (search.trim()) params.search = search.trim();
      const res = await axios.get(`${API}/blog`, { params });
      setPosts(res.data.posts);
      setTotalPages(res.data.pages);
      setTotal(res.data.total);
    } catch (err) {
      console.error('Error fetching blog posts:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchPosts(); }, [page, activeCategory]);

  const handleSearch = (e) => {
    e.preventDefault();
    setPage(1);
    fetchPosts();
  };

  const handleCategoryChange = (cat) => {
    setActiveCategory(cat);
    setPage(1);
    setSearch('');
  };

  return (
    <div className="min-h-screen bg-white">
      <Helmet>
        <title>Blog &amp; Resources - WorkNation Labour Ltd | BC Staffing Industry Insights</title>
        <meta name="description" content="Stay informed with WorkNation Labour Ltd's blog. Industry insights, career tips, safety guides, and labour market trends for construction, warehouse, agriculture, transportation, and cold storage workers and employers in British Columbia." />
        <link rel="canonical" href="https://worknation.ca/blog" />
      </Helmet>
      <Header />
      <main>
        {/* Hero */}
        <section className="pt-28 pb-10 px-6 bg-gradient-to-b from-gray-50 to-white">
          <div className="max-w-5xl mx-auto text-center">
            <h1 className="text-4xl sm:text-5xl font-bold text-[#003366] mb-4">Blog &amp; Resources</h1>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto mb-8">
              Industry insights, career tips, and labour market updates for employers and workers across British Columbia.
            </p>

            {/* Search */}
            <form onSubmit={handleSearch} className="max-w-lg mx-auto flex gap-2" data-testid="blog-search-form">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
                <Input
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Search articles..."
                  className="pl-10 border-gray-300 focus:border-[#003366] focus:ring-[#003366]"
                  data-testid="blog-search-input"
                />
              </div>
              <Button type="submit" className="bg-[#003366] hover:bg-[#004488] text-white px-6" data-testid="blog-search-btn">
                Search
              </Button>
            </form>
          </div>
        </section>

        {/* Category Filters */}
        <section className="px-6 pb-8">
          <div className="max-w-5xl mx-auto">
            <div className="flex items-center gap-2 overflow-x-auto pb-2" data-testid="blog-category-filters">
              <Filter size={16} className="text-gray-400 flex-shrink-0" aria-hidden="true" />
              {categories.map((cat) => (
                <button
                  key={cat}
                  onClick={() => handleCategoryChange(cat)}
                  className={`px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-colors duration-200 ${
                    activeCategory === cat
                      ? 'bg-[#003366] text-white'
                      : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                  }`}
                  data-testid={`blog-filter-${cat.toLowerCase().replace(/\s+/g, '-')}`}
                >
                  {cat}
                </button>
              ))}
            </div>
          </div>
        </section>

        {/* Posts Grid */}
        <section className="px-6 pb-16">
          <div className="max-w-6xl mx-auto">
            {loading ? (
              <div className="flex justify-center py-20">
                <Loader2 className="animate-spin text-[#003366]" size={40} />
              </div>
            ) : posts.length === 0 ? (
              <div className="text-center py-20">
                <p className="text-lg text-gray-500 mb-4">No articles found.</p>
                <Button onClick={() => { setSearch(''); setActiveCategory('All'); setPage(1); }} className="bg-[#003366] text-white">
                  View All Posts
                </Button>
              </div>
            ) : (
              <>
                <p className="text-sm text-gray-500 mb-6">{total} article{total !== 1 ? 's' : ''} found</p>
                <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
                  {posts.map((post) => (
                    <BlogCard key={post.id} post={post} />
                  ))}
                </div>

                {/* Pagination */}
                {totalPages > 1 && (
                  <div className="flex justify-center items-center gap-3 mt-12" data-testid="blog-pagination">
                    <Button
                      variant="outline"
                      onClick={() => setPage((p) => Math.max(1, p - 1))}
                      disabled={page <= 1}
                      className="border-gray-300"
                    >
                      <ChevronLeft size={18} /> Previous
                    </Button>
                    <span className="text-sm text-gray-600">
                      Page {page} of {totalPages}
                    </span>
                    <Button
                      variant="outline"
                      onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                      disabled={page >= totalPages}
                      className="border-gray-300"
                    >
                      Next <ChevronRight size={18} />
                    </Button>
                  </div>
                )}
              </>
            )}
          </div>
        </section>

        <CTABanner />
      </main>
      <Footer />
    </div>
  );
};

export default BlogPage;
